/**
 * NotificationSettings: notification preferences page, rendered at
 * /account/notifications (nested under UserHomepage). Renders toggle sections for
 * email and SMS alerts (the email section is hidden until the user has an email),
 * persisting changes via api/user (UserAPI.updateNotificationSettings) and syncing
 * the returned user into contexts/UserContext. On an "Unauthorized" error it
 * clears the user context and redirects to /signUpLogin; other errors surface
 * through contexts/ErrorContext. Laid out with components/SubheadedPage.
 */
import {
    clearUserContext,
    setUserContext,
    useUserDispatch,
    useUserState,
} from "@/contexts/UserContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserAPI } from "@/api/user";
import { SubheadedPage } from "@/components/SubheadedPage";
import { useError } from "@/contexts/ErrorContext";

type NotificationType = "emailCommEnabled" | "smsCommEnabled";

interface NotificationSetting {
    id: string;
    title: string;
    description: string;
}

interface NotificationSection {
    title: string;
    type: NotificationType;
    description: string;
    settings: NotificationSetting[];
    visibile: boolean;
}

const NotificationSettings = () => {
    const userDispatch = useUserDispatch();
    const currentUser = useUserState();
    const navigate = useNavigate();
    const { showError } = useError();
    const [loading, setLoading] = useState<boolean>(false);
    const [emailEnabled, setEmailEnabled] = useState<boolean>(currentUser!.emailCommEnabled);
    const [textEnabled, setTextEnabled] = useState<boolean>(currentUser!.smsCommEnabled);

    const emailSettings: NotificationSection = {
        title: "email notifications",
        type: "emailCommEnabled",
        description: "configure your email notification preferences",
        settings: [
            {
                id: "email",
                title: "porsui alerts",
                description:
                    "receive emails when somebody submits for your listed bounty, somebody adds your bounty to their tracking, or if someone accepts your submission",
            },
        ],
        visibile: Boolean(currentUser!.email),
    };

    const textSettings: NotificationSection = {
        title: "text notifications",
        type: "smsCommEnabled",
        description: "configure your sms notification preferences",
        settings: [
            {
                id: "sms",
                title: "time-sensitive alerts",
                description:
                    "receive a text when somebody submits for your listed bounty, which could get bought in the meantime",
            },
        ],
        visibile: true,
    };

    const handleSettingsChange = async (emailCommEnabled: boolean, smsCommEnabled: boolean) => {
        setLoading(true);
        try {
            const { user } = await UserAPI.updateNotificationSettings(
                emailCommEnabled,
                smsCommEnabled
            );
            setUserContext(userDispatch, user);
        } catch (errMessage) {
            if (errMessage === "Unauthorized") {
                clearUserContext(userDispatch);
                navigate("/signUpLogin");
            }
            showError(errMessage as string);
        } finally {
            setLoading(false);
        }
    };

    const setEnabledForNotificationType = (type: NotificationType, enabled: boolean) => {
        switch (type) {
            case "emailCommEnabled":
                setEmailEnabled(enabled);
                handleSettingsChange(enabled, textEnabled);
                break;
            case "smsCommEnabled":
                setTextEnabled(enabled);
                handleSettingsChange(emailEnabled, enabled);
                break;
        }
    };

    const renderSection = (section: NotificationSection, enabled: boolean) => (
        <div
            className={`bg-white rounded-lg shadow p-6 mb-6 flex-1 flex flex-col h-full ${
                section.visibile ? "visible" : "hidden"
            }`}
        >
            <div className="mb-4">
                <h2 className="text-xl font-semibold">{section.title}</h2>
                <p className="text-sm text-gray-500">{section.description}</p>
            </div>
            <div className="flex-1 flex items-center justify-center">
                <div className="space-y-4">
                    {section.settings.map((setting) => (
                        <div key={setting.id} className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={enabled}
                                        onChange={(e) => {
                                            setEnabledForNotificationType(
                                                section.type,
                                                e.target.checked
                                            );
                                        }}
                                        disabled={loading}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-slate-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                                </label>
                                <div className="space-y-1">
                                    <label htmlFor={setting.id} className="text-sm font-medium">
                                        {setting.title}
                                    </label>
                                    <p className="text-sm text-gray-500">{setting.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <SubheadedPage heading="notification settings" headerColor="bg-slate-50">
            <div className="flex flex-col md:items-start items-center min-w-16 max-w-2xl p-8">
                {renderSection(emailSettings, emailEnabled)}
                {renderSection(textSettings, textEnabled)}
            </div>
        </SubheadedPage>
    );
};

export default NotificationSettings;
